import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  EyeIcon,
  EyeSlashIcon,
  UserIcon,
  LockClosedIcon,
  EnvelopeIcon,
  PhoneIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import { useRegisterMutation } from '../../services/authApi';
import { selectAuthError, selectAuthLoading, clearError } from '../../store/slices/authSlice';

const Register = () => {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [register] = useRegisterMutation();
  const authError = useSelector(selectAuthError);
  const authLoading = useSelector(selectAuthLoading);

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .min(2, t('auth:firstNameMinLength'))
      .required(t('auth:firstNameRequired')),
    lastName: Yup.string()
      .min(2, t('auth:lastNameMinLength'))
      .required(t('auth:lastNameRequired')),
    email: Yup.string()
      .email(t('auth:invalidEmail'))
      .required(t('auth:emailRequired')),
    phone: Yup.string()
      .matches(/^\+?[\d\s\-\(\)]+$/, t('auth:invalidPhone'))
      .required(t('auth:phoneRequired')),
    password: Yup.string()
      .min(8, t('auth:passwordMinLength'))
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, t('auth:passwordRequirements'))
      .required(t('auth:passwordRequired')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], t('auth:passwordMismatch'))
      .required(t('auth:confirmPasswordRequired')),
    agreeToTerms: Yup.boolean()
      .oneOf([true], t('auth:termsRequired')),
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      agreeToTerms: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        dispatch(clearError());
        await register({
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          password: values.password,
        }).unwrap();

        // Success - navigate to login with success message
        navigate('/login', {
          state: {
            message: t('auth:registrationSuccess'),
            email: values.email
          }
        });
      } catch (error) {
        // Error is handled by RTK Query and stored in auth state
        console.error('Registration failed:', error);
      }
    },
  });

  // Clear error when component unmounts or when user starts typing
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  useEffect(() => {
    if (Object.values(formik.values).some(value => value !== '' && value !== false)) {
      dispatch(clearError());
    }
  }, [formik.values, dispatch]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 animate-fade-in">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center animate-fade-in-up">
          <div className="mx-auto h-16 w-16 bg-gradient-primary rounded-2xl flex items-center justify-center shadow-glow animate-bounce-gentle">
            <UserPlusIcon className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-display font-bold text-secondary-900">
            {t('auth:registerTitle')}
          </h2>
          <p className="mt-2 text-sm text-secondary-600">
            {t('auth:registerSubtitle')}
          </p>
        </div>

        {/* Registration Form */}
        <div className="card card-glass animate-fade-in-up" style={{animationDelay: '0.2s'}}>
          <div className="card-body">
            <form className="space-y-6" onSubmit={formik.handleSubmit}>
              {/* Error Message */}
              {authError && (
                <div className="rounded-lg bg-error-50 border border-error-200 p-4 animate-fade-in">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-5 w-5 text-error-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-error-800">{authError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                {/* First Name */}
                <div className="animate-fade-in-up" style={{animationDelay: '0.3s'}}>
                  <label htmlFor="firstName" className="form-label">
                    {t('auth:firstNameLabel')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <UserIcon className="h-5 w-5 text-secondary-400" />
                    </div>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      autoComplete="given-name"
                      className={`form-input pl-10 ${
                        formik.touched.firstName && formik.errors.firstName
                          ? 'border-error-300 focus:ring-error-500 focus:border-error-500'
                          : ''
                      }`}
                      placeholder={t('auth:firstNamePlaceholder')}
                      value={formik.values.firstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.touched.firstName && formik.errors.firstName && (
                    <p className="form-error">{formik.errors.firstName}</p>
                  )}
                </div>

                {/* Last Name */}
                <div className="animate-fade-in-up" style={{animationDelay: '0.35s'}}>
                  <label htmlFor="lastName" className="form-label">
                    {t('auth:lastNameLabel')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <UserIcon className="h-5 w-5 text-secondary-400" />
                    </div>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      autoComplete="family-name"
                      className={`form-input pl-10 ${
                        formik.touched.lastName && formik.errors.lastName
                          ? 'border-error-300 focus:ring-error-500 focus:border-error-500'
                          : ''
                      }`}
                      placeholder={t('auth:lastNamePlaceholder')}
                      value={formik.values.lastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.touched.lastName && formik.errors.lastName && (
                    <p className="form-error">{formik.errors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Email Field */}
              <div className="animate-fade-in-up" style={{animationDelay: '0.4s'}}>
                <label htmlFor="email" className="form-label">
                  {t('auth:emailLabel')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className={`form-input pl-10 ${
                      formik.touched.email && formik.errors.email
                        ? 'border-error-300 focus:ring-error-500 focus:border-error-500'
                        : ''
                    }`}
                    placeholder={t('auth:emailPlaceholder')}
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                {formik.touched.email && formik.errors.email && (
                  <p className="form-error">{formik.errors.email}</p>
                )}
              </div>

              {/* Phone Field */}
              <div className="animate-fade-in-up" style={{animationDelay: '0.45s'}}>
                <label htmlFor="phone" className="form-label">
                  {t('auth:phoneLabel')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <PhoneIcon className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    className={`form-input pl-10 ${
                      formik.touched.phone && formik.errors.phone
                        ? 'border-error-300 focus:ring-error-500 focus:border-error-500'
                        : ''
                    }`}
                    placeholder={t('auth:phonePlaceholder')}
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                {formik.touched.phone && formik.errors.phone && (
                  <p className="form-error">{formik.errors.phone}</p>
                )}
              </div>

              {/* Password Field */}
              <div className="animate-fade-in-up" style={{animationDelay: '0.5s'}}>
                <label htmlFor="password" className="form-label">
                  {t('auth:passwordLabel')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className={`form-input pl-10 pr-10 ${
                      formik.touched.password && formik.errors.password
                        ? 'border-error-300 focus:ring-error-500 focus:border-error-500'
                        : ''
                    }`}
                    placeholder={t('auth:passwordPlaceholder')}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      className="text-secondary-400 hover:text-secondary-600 focus:outline-none focus:text-secondary-600 transition-colors duration-200"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <p className="form-error">{formik.errors.password}</p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div className="animate-fade-in-up" style={{animationDelay: '0.55s'}}>
                <label htmlFor="confirmPassword" className="form-label">
                  {t('auth:confirmPasswordLabel')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className={`form-input pl-10 pr-10 ${
                      formik.touched.confirmPassword && formik.errors.confirmPassword
                        ? 'border-error-300 focus:ring-error-500 focus:border-error-500'
                        : ''
                    }`}
                    placeholder={t('auth:confirmPasswordPlaceholder')}
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      className="text-secondary-400 hover:text-secondary-600 focus:outline-none focus:text-secondary-600 transition-colors duration-200"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
                {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                  <p className="form-error">{formik.errors.confirmPassword}</p>
                )}
              </div>

              {/* Terms and Conditions */}
              <div className="animate-fade-in-up" style={{animationDelay: '0.6s'}}>
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="agreeToTerms"
                      name="agreeToTerms"
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                      checked={formik.values.agreeToTerms}
                      onChange={formik.handleChange}
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="agreeToTerms" className="text-secondary-700">
                      {t('auth:agreeToTerms')}{' '}
                      <Link
                        to="/terms"
                        className="text-primary-600 hover:text-primary-500 font-medium"
                      >
                        {t('auth:termsOfService')}
                      </Link>{' '}
                      {t('auth:and')}{' '}
                      <Link
                        to="/privacy"
                        className="text-primary-600 hover:text-primary-500 font-medium"
                      >
                        {t('auth:privacyPolicy')}
                      </Link>
                    </label>
                    {formik.touched.agreeToTerms && formik.errors.agreeToTerms && (
                      <p className="form-error mt-1">{formik.errors.agreeToTerms}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="animate-fade-in-up" style={{animationDelay: '0.65s'}}>
                <button
                  type="submit"
                  disabled={authLoading || !formik.isValid}
                  className="btn-primary w-full py-3 text-base font-medium group relative overflow-hidden"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    {authLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('auth:creatingAccount')}
                      </>
                    ) : (
                      <>
                        {t('auth:registerButton')}
                        <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                      </>
                    )}
                  </span>
                  <div className="absolute inset-0 bg-primary-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Login Link */}
        <div className="text-center animate-fade-in-up" style={{animationDelay: '0.7s'}}>
          <p className="text-sm text-secondary-600">
            {t('auth:alreadyHaveAccount')}{' '}
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500 transition-colors duration-200"
            >
              {t('auth:signInHere')}
            </Link>
          </p>
        </div>

        {/* Footer */}
        <div className="text-center animate-fade-in-up" style={{animationDelay: '0.8s'}}>
          <p className="text-sm text-secondary-600">
            © 2025 IAR HRMS. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
